import { Suspense, type ReactNode } from 'react'
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import {
  PUBLIC_PATH_PREFIX,
  isPublicEventsEnabled,
  isSeasonRecapEnabled,
} from '@/shared/config'
import { PrivateRoute, PublicRoute, RequireOrgMembership } from '@/modules/auth/ui/components'
import { DashboardShellRoute } from './dashboard-shell-route'
import { SignInRoute } from './sign-in-route'
import { SignupRoute } from './signup-route'
import { RequirePersona } from './require-persona'
import { RequireClubFeatures } from './require-club-features'
import { lazyRoute } from './lazy-route'

const AuthLayoutPreview = lazyRoute(() =>
  import('@/modules/auth/ui/components/auth-layout-preview').then((m) => ({
    default: m.AuthLayoutPreview,
  })),
)
const SeatManagementPage = lazyRoute(() =>
  import('@/modules/members/ui').then((m) => ({ default: m.SeatManagementPage })),
)
const NotFoundPage = lazyRoute(() =>
  import('@/modules/auth/ui').then((m) => ({ default: m.NotFoundPage })),
)
const AuthCallbackRoute = lazyRoute(() =>
  import('./auth-callback-route').then((m) => ({ default: m.AuthCallbackRoute })),
)
const AuthCodeErrorRoute = lazyRoute(() =>
  import('./auth-code-error-route').then((m) => ({ default: m.AuthCodeErrorRoute })),
)
const CompleteSetupRoute = lazyRoute(() =>
  import('./complete-setup-route').then((m) => ({ default: m.CompleteSetupRoute })),
)
const DashboardRoute = lazyRoute(() =>
  import('./dashboard-route').then((m) => ({ default: m.DashboardRoute })),
)
const NoAccessRoute = lazyRoute(() =>
  import('./no-access-route').then((m) => ({ default: m.NoAccessRoute })),
)
const ActivityFeedRoute = lazyRoute(() =>
  import('./activity-feed-route').then((m) => ({ default: m.ActivityFeedRoute })),
)
const AnalyticsDashboardRoute = lazyRoute(() =>
  import('./analytics-dashboard-route').then((m) => ({ default: m.AnalyticsDashboardRoute })),
)
const ArchivesRoute = lazyRoute(() =>
  import('./archives-route').then((m) => ({ default: m.ArchivesRoute })),
)
const SeasonRecapRoute = lazyRoute(() =>
  import('./season-recap-route').then((m) => ({ default: m.SeasonRecapRoute })),
)
const BookingDetailRoute = lazyRoute(() =>
  import('./booking-detail-route').then((m) => ({ default: m.BookingDetailRoute })),
)
const BookingsListRoute = lazyRoute(() =>
  import('./bookings-list-route').then((m) => ({ default: m.BookingsListRoute })),
)
const ClientsRoute = lazyRoute(() =>
  import('./clients-route').then((m) => ({ default: m.ClientsRoute })),
)
const RosterImportRoute = lazyRoute(() =>
  import('./roster-import-route').then((m) => ({ default: m.RosterImportRoute })),
)
const CustomerImportRoute = lazyRoute(() =>
  import('./customer-import-route').then((m) => ({ default: m.CustomerImportRoute })),
)
const DoghouseRoute = lazyRoute(() =>
  import('./doghouse-route').then((m) => ({ default: m.DoghouseRoute })),
)
const ExpenseSettingsRoute = lazyRoute(() =>
  import('./expense-settings-route').then((m) => ({ default: m.ExpenseSettingsRoute })),
)
const BookingPackagesRoute = lazyRoute(() =>
  import('./booking-packages-route').then((m) => ({ default: m.BookingPackagesRoute })),
)
const ExpenseManagerRoute = lazyRoute(() =>
  import('./expense-manager-route').then((m) => ({ default: m.ExpenseManagerRoute })),
)
const FinancesRedirectRoute = lazyRoute(() =>
  import('./finances-redirect-route').then((m) => ({ default: m.FinancesRedirectRoute })),
)
const FinancesReportsRoute = lazyRoute(() =>
  import('./finances-reports-route').then((m) => ({ default: m.FinancesReportsRoute })),
)
const InvoicesRoute = lazyRoute(() =>
  import('./invoices-route').then((m) => ({ default: m.InvoicesRoute })),
)
const MemberLedgerRoute = lazyRoute(() =>
  import('./member-ledger-route').then((m) => ({ default: m.MemberLedgerRoute })),
)
const GalleryRoute = lazyRoute(() =>
  import('./gallery-route').then((m) => ({ default: m.GalleryRoute })),
)
const HuntDetailRoute = lazyRoute(() =>
  import('./hunt-detail-route').then((m) => ({ default: m.HuntDetailRoute })),
)
const HuntLogsListRoute = lazyRoute(() =>
  import('./hunt-logs-list-route').then((m) => ({ default: m.HuntLogsListRoute })),
)
const InventoryRoute = lazyRoute(() =>
  import('./inventory-route').then((m) => ({ default: m.InventoryRoute })),
)
const EquipmentCategoriesRoute = lazyRoute(() =>
  import('./equipment-categories-route').then((m) => ({ default: m.EquipmentCategoriesRoute })),
)
const JoinRoute = lazyRoute(() => import('./join-route').then((m) => ({ default: m.JoinRoute })))
const PayInvoiceRoute = lazyRoute(() =>
  import('./pay-invoice-route').then((m) => ({ default: m.PayInvoiceRoute })),
)
const LandingRoute = lazyRoute(() =>
  import('./landing-route').then((m) => ({ default: m.LandingRoute })),
)
const LinkClubRoute = lazyRoute(() =>
  import('./link-club-route').then((m) => ({ default: m.LinkClubRoute })),
)
const LoginRedirectRoute = lazyRoute(() =>
  import('./login-redirect-route').then((m) => ({ default: m.LoginRedirectRoute })),
)
const MagicLinkRoute = lazyRoute(() =>
  import('./magic-link-route').then((m) => ({ default: m.MagicLinkRoute })),
)
const MessagesRoute = lazyRoute(() =>
  import('./messages-route').then((m) => ({ default: m.MessagesRoute })),
)
const PrivacyPolicyRoute = lazyRoute(() =>
  import('./privacy-policy-route').then((m) => ({ default: m.PrivacyPolicyRoute })),
)
const PublicSiteRoute = lazyRoute(() =>
  import('./public-site-route').then((m) => ({ default: m.PublicSiteRoute })),
)
const PublicEventRoute = lazyRoute(() =>
  import('./public-event-route').then((m) => ({ default: m.PublicEventRoute })),
)
const PublicQrRoute = lazyRoute(() =>
  import('./public-qr-route').then((m) => ({ default: m.PublicQrRoute })),
)
const RegistrationDocumentsRoute = lazyRoute(() =>
  import('./registration-documents-route').then((m) => ({
    default: m.RegistrationDocumentsRoute,
  })),
)
const EventsRoute = lazyRoute(() => import('./events-route').then((m) => ({ default: m.EventsRoute })))
const EventDetailRoute = lazyRoute(() =>
  import('./event-detail-route').then((m) => ({ default: m.EventDetailRoute })),
)
const PropertiesGatewayRoute = lazyRoute(() =>
  import('./properties-gateway-route').then((m) => ({ default: m.PropertiesGatewayRoute })),
)
const ProPaywallRoute = lazyRoute(() =>
  import('./pro-paywall-route').then((m) => ({ default: m.ProPaywallRoute })),
)
const PropertyManagementRoute = lazyRoute(() =>
  import('./property-management-route').then((m) => ({ default: m.PropertyManagementRoute })),
)
const RegisterRoute = lazyRoute(() =>
  import('./register-route').then((m) => ({ default: m.RegisterRoute })),
)
const RegisterCompleteRoute = lazyRoute(() =>
  import('./register-complete-route').then((m) => ({ default: m.RegisterCompleteRoute })),
)
const ReportsRoute = lazyRoute(() =>
  import('./reports-route').then((m) => ({ default: m.ReportsRoute })),
)
const ResubscribeRoute = lazyRoute(() =>
  import('./resubscribe-route').then((m) => ({ default: m.ResubscribeRoute })),
)
const ScheduleRoute = lazyRoute(() =>
  import('./schedule-route').then((m) => ({ default: m.ScheduleRoute })),
)
const SettingsRoute = lazyRoute(() =>
  import('./settings-route').then((m) => ({ default: m.SettingsRoute })),
)
const TermsOfServiceRoute = lazyRoute(() =>
  import('./terms-of-service-route').then((m) => ({ default: m.TermsOfServiceRoute })),
)
const ImpersonationHandoffRoute = lazyRoute(() =>
  import('./impersonation-handoff-route').then((m) => ({ default: m.ImpersonationHandoffRoute })),
)
const TransferRoute = lazyRoute(() =>
  import('./transfer-route').then((m) => ({ default: m.TransferRoute })),
)
const VerifyEmailRoute = lazyRoute(() =>
  import('./verify-email-route').then((m) => ({ default: m.VerifyEmailRoute })),
)
const TodosRoute = lazyRoute(() => import('./todos-route').then((m) => ({ default: m.TodosRoute })))
const FeedbackHistoryRoute = lazyRoute(() =>
  import('./feedback-history-route').then((m) => ({ default: m.FeedbackHistoryRoute })),
)
const FeedbackDetailRoute = lazyRoute(() =>
  import('./feedback-detail-route').then((m) => ({ default: m.FeedbackDetailRoute })),
)
const NotificationsSettingsRoute = lazyRoute(() =>
  import('./notifications-settings-route').then((m) => ({ default: m.NotificationsSettingsRoute })),
)
const WaiverTemplateDetailRoute = lazyRoute(() =>
  import('./waiver-template-detail-route').then((m) => ({ default: m.WaiverTemplateDetailRoute })),
)
const WaiverTemplateEditRoute = lazyRoute(() =>
  import('./waiver-template-edit-route').then((m) => ({ default: m.WaiverTemplateEditRoute })),
)
const WaiverUploadRoute = lazyRoute(() =>
  import('./waiver-upload-route').then((m) => ({ default: m.WaiverUploadRoute })),
)
const EdocsRoute = lazyRoute(() =>
  import('./edocs-route').then((m) => ({ default: m.EdocsRoute })),
)
const EdocsQrRoute = lazyRoute(() =>
  import('./edocs-qr-route').then((m) => ({ default: m.EdocsQrRoute })),
)
const EdocsQrDetailRoute = lazyRoute(() =>
  import('./edocs-qr-detail-route').then((m) => ({ default: m.EdocsQrDetailRoute })),
)
const LegacyWaiversRedirectRoute = lazyRoute(() =>
  import('./legacy-waivers-redirect-route').then((m) => ({
    default: m.LegacyWaiversRedirectRoute,
  })),
)

function RouteFallback() {
  return (
    <div className="flex h-full min-h-[40vh] w-full items-center justify-center">
      <Loader2 aria-label="Loading" className="size-6 animate-spin text-muted-foreground" />
    </div>
  )
}

function withSuspense(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>
}

export const routes: RouteObject[] = [
  { path: '/', element: withSuspense(<LandingRoute />) },
  { path: '/login', element: withSuspense(<LoginRedirectRoute />) },
  { path: '/auth/callback', element: withSuspense(<AuthCallbackRoute />) },
  { path: '/auth/auth-code-error', element: withSuspense(<AuthCodeErrorRoute />) },
  { path: '/auth/complete-setup', element: withSuspense(<CompleteSetupRoute />) },
  { path: '/auth/link-club', element: withSuspense(<LinkClubRoute />) },
  { path: '/auth/magic-link', element: withSuspense(<MagicLinkRoute />) },
  { path: '/auth/transfer', element: withSuspense(<TransferRoute />) },
  { path: '/impersonation/handoff', element: withSuspense(<ImpersonationHandoffRoute />) },
  { path: '/transfer', element: withSuspense(<TransferRoute />) },
  { path: '/auth/verify-email', element: withSuspense(<VerifyEmailRoute />) },
  { path: '/terms-of-service', element: withSuspense(<TermsOfServiceRoute />) },
  { path: '/privacy-policy', element: withSuspense(<PrivacyPolicyRoute />) },
  { path: '/register', element: withSuspense(<RegisterRoute />) },
  { path: '/register/complete', element: withSuspense(<RegisterCompleteRoute />) },
  { path: '/pro', element: withSuspense(<ProPaywallRoute />) },
  { path: '/join/:token', element: withSuspense(<JoinRoute />) },
  { path: '/pay/:invoiceId', element: withSuspense(<PayInvoiceRoute />) },
  {
    path: `${PUBLIC_PATH_PREFIX}/qr/:codeId`,
    element: withSuspense(<PublicQrRoute />),
  },
  {
    path: `${PUBLIC_PATH_PREFIX}/qr/:codeId/signers/:signerId`,
    element: withSuspense(<PublicQrRoute />),
  },
  ...(isPublicEventsEnabled()
    ? [
        { path: `${PUBLIC_PATH_PREFIX}/:slug`, element: withSuspense(<PublicSiteRoute />) },
      ]
    : []),
  ...(isPublicEventsEnabled()
    ? [
        {
          path: `${PUBLIC_PATH_PREFIX}/:slug/events/:eventId`,
          element: withSuspense(<PublicEventRoute />),
        },
        {
          path: `${PUBLIC_PATH_PREFIX}/:slug/events/:eventId/registrations/:registrationId/documents`,
          element: withSuspense(<RegistrationDocumentsRoute />),
        },
      ]
    : []),
  {
    element: <PublicRoute />,
    children: [
      {
        path: '/sign-in',
        element: <SignInRoute />,
      },
      {
        path: '/signup',
        element: <SignupRoute />,
      },
    ],
  },
  {
    element: <PrivateRoute />,
    children: [
      { path: '/no-access', element: withSuspense(<NoAccessRoute />) },
      { path: '/resubscribe/:orgId', element: withSuspense(<ResubscribeRoute />) },
      {
        element: <RequireOrgMembership />,
        children: [
          {
            element: (
              <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                <DashboardShellRoute />
              </RequirePersona>
            ),
            children: [
              {
                path: '/dashboard',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <DashboardRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/activity',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <ActivityFeedRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/todos',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <TodosRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/feedback',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <FeedbackHistoryRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/feedback/:id',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <FeedbackDetailRoute />
                  </RequirePersona>,
                ),
              },
              { path: '/account/notifications', element: withSuspense(<NotificationsSettingsRoute />) },
              {
                path: '/doghouse',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <DoghouseRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/gallery',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <GalleryRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/members',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <SeatManagementPage />
                  </RequirePersona>,
                ),
              },
              {
                path: '/members/import',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <RosterImportRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/clients',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner']}>
                    <ClientsRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/clients/import',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner']}>
                    <CustomerImportRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/messages',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner', 'club-manager']}>
                    <MessagesRoute />
                  </RequirePersona>,
                ),
              },
              { path: '/finances', element: withSuspense(<FinancesRedirectRoute />) },
              {
                path: '/finances/invoices',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner']}>
                    <InvoicesRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/finances/member-ledger',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner', 'club-manager']}>
                    <RequireClubFeatures>
                      <MemberLedgerRoute />
                    </RequireClubFeatures>
                  </RequirePersona>,
                ),
              },
              {
                path: '/finances/booking-packages',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner']}>
                    <BookingPackagesRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/finances/expenses',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner', 'club-manager']}>
                    <ExpenseManagerRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/finances/reports',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner', 'club-manager']}>
                    <FinancesReportsRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/properties',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <PropertiesGatewayRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/properties/new',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <PropertyManagementRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/properties/:propertyId',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <PropertyManagementRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/inventory',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <InventoryRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/inventory/categories',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <EquipmentCategoriesRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/hunt-logs',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <HuntLogsListRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/hunt-logs/:huntId',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <HuntDetailRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/analytics',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <AnalyticsDashboardRoute />
                  </RequirePersona>,
                ),
              },
              ...(isSeasonRecapEnabled()
                ? [
                    {
                      path: '/season-recap',
                      element: withSuspense(
                        <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                          <SeasonRecapRoute />
                        </RequirePersona>,
                      ),
                    },
                  ]
                : []),
              {
                path: '/historical',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <ArchivesRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/reports',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <ReportsRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/schedule',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <ScheduleRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/bookings',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner']}>
                    <BookingsListRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/bookings/pipeline',
                element: <Navigate to="/bookings" replace />,
              },
              {
                path: '/bookings/:bookingId',
                element: withSuspense(
                  <RequirePersona allowed={['outfitter-owner']}>
                    <BookingDetailRoute />
                  </RequirePersona>,
                ),
              },
              ...(isPublicEventsEnabled()
                ? [
                    {
                      path: '/events',
                      element: withSuspense(
                        <RequirePersona allowed={['outfitter-owner']}>
                          <EventsRoute />
                        </RequirePersona>,
                      ),
                    },
                    {
                      path: '/events/:eventId',
                      element: withSuspense(
                        <RequirePersona allowed={['outfitter-owner']}>
                          <EventDetailRoute />
                        </RequirePersona>,
                      ),
                    },
                  ]
                : []),
              {
                path: '/settings',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <SettingsRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/settings/waivers/*',
                element: withSuspense(<LegacyWaiversRedirectRoute />),
              },
              {
                path: '/edocs',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <EdocsRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/edocs/upload',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <WaiverUploadRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/edocs/qr',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <EdocsQrRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/edocs/qr/:qrId',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <EdocsQrDetailRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/edocs/:templateId',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <WaiverTemplateDetailRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/edocs/:templateId/edit',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <WaiverTemplateEditRoute />
                  </RequirePersona>,
                ),
              },
              {
                path: '/settings/expenses',
                element: withSuspense(
                  <RequirePersona allowed={['club-manager', 'outfitter-owner']}>
                    <ExpenseSettingsRoute />
                  </RequirePersona>,
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  ...(import.meta.env.DEV
    ? [{ path: '/auth-layout-preview', element: withSuspense(<AuthLayoutPreview />) }]
    : []),
  { path: '*', element: withSuspense(<NotFoundPage />) },
]

export const router = createBrowserRouter(routes)
